import * as yup from "yup";
import { differenceInCalendarDays } from "date-fns";
import {
  DaysOfWeek,
  EventType,
  RecurringPeriodType,
} from "../../../../../common/enums";
import {
  fieldMustBePositive,
  requiredField,
} from "../../../../../common/infrastructure/helpers/yupHelper";
import { MAX_FULL_DAY_BLOCKED_DAYS } from "../utils/fullDayBlockedTimeUtility";

export const blockedTimeWizardFormSchema = yup
  .object({
    id: yup.number().defined().optional(),
    studioId: yup.number().defined().required(),
    breakTypeId: yup
      .number()
      .defined()
      .required(requiredField("Тип блокирано време")),
    employeeId: yup.number().defined().required(requiredField("Служител")),
    date: yup
      .date()
      .defined()
      .typeError("Моля изберете валидни дата и час.")
      .required(requiredField("Дата и час")),
    durationInMinutes: yup
      .number()
      .defined()
      .positive(fieldMustBePositive("Продължителност"))
      .required(requiredField("Продължителност")),
    type: yup.mixed().defined().required(),

    // Full-day mode: blocks 00:00-24:00 on every day from date to endDate
    isFullDay: yup.boolean().defined().default(false),
    endDate: yup
      .date()
      .nullable()
      .optional()
      .test(
        "end-not-before-start",
        "Крайната дата не може да е преди началната.",
        function (endDate) {
          const { isFullDay, date } = this.parent;
          return !isFullDay || !endDate || !date || endDate >= date;
        },
      )
      .test(
        "range-not-too-long",
        `Може да блокирате най-много ${MAX_FULL_DAY_BLOCKED_DAYS} дни наведнъж.`,
        function (endDate) {
          const { isFullDay, date } = this.parent;
          return (
            !isFullDay ||
            !endDate ||
            !date ||
            differenceInCalendarDays(endDate, date) < MAX_FULL_DAY_BLOCKED_DAYS
          );
        },
      ),

    // Recurring validation
    isRecurring: yup.boolean().defined().default(false),
    recurringDays: yup
      .array()
      .defined()
      .of(yup.number())
      .when("isRecurring", {
        is: true,
        then: (schema) =>
          schema.min(1, "Моля изберете поне един ден от седмицата"),
        otherwise: (schema) => schema.optional(),
      }),
    recurringPeriod: yup
      .number()
      .defined()
      .when("isRecurring", {
        is: true,
        then: (schema) =>
          schema
            .min(1, "Периодът трябва да бъде поне 1")
            .required(requiredField("Период"))
            .when("recurringPeriodType", {
              is: RecurringPeriodType.Months,
              then: (schema) =>
                schema.max(3, "Периодът не може да бъде повече от 3 месеца"),
              otherwise: (schema) =>
                schema.max(12, "Периодът не може да бъде повече от 12 седмици"),
            }),
        otherwise: (schema) => schema.optional(),
      }),
    recurringPeriodType: yup
      .number()
      .defined()
      .when("isRecurring", {
        is: true,
        then: (schema) => schema.required(requiredField("Тип период")),
        otherwise: (schema) => schema.optional(),
      }),
    recurringInterval: yup
      .number()
      .defined()
      .when("isRecurring", {
        is: true,
        then: (schema) =>
          schema
            .min(1, "Интервалът трябва да бъде поне 1")
            .required(requiredField("Интервал"))
            .max(24, "Интервалът не може да бъде повече от 24"),
        otherwise: (schema) => schema.optional(),
      }),
    recurringIntervalType: yup
      .number()
      .defined()
      .when("isRecurring", {
        is: true,
        then: (schema) => schema.required(requiredField("Тип интервал")),
        otherwise: (schema) => schema.optional(),
      }),
    applyChangeToAllFutureEvents: yup.boolean().defined().optional(),
  })
  .required();

export type IBlockedTimeWizardFormModel = yup.InferType<
  typeof blockedTimeWizardFormSchema
>;

export const blockedTimeWizardFormDefaultValues: IBlockedTimeWizardFormModel =
  {
    id: 0,
    studioId: 0,
    breakTypeId: 0,
    employeeId: 0,
    date: undefined,
    durationInMinutes: 60,
    type: EventType.BlockedTime,
    isFullDay: false,
    endDate: undefined,
    isRecurring: false,
    recurringDays: [DaysOfWeek.Monday],
    recurringPeriod: 1,
    recurringPeriodType: RecurringPeriodType.Months,
    recurringInterval: 1,
    recurringIntervalType: RecurringPeriodType.Weeks,
  };
